import {
  fmtEur,
  settlements,
  settlementSnapshot,
  splitAmount,
} from "@/shared/data";
import { icon } from "@/shared/icons";
import type { Route } from "@/shared/layout";

interface Ctx {
  canvas: HTMLElement;
  navigate: (r: Route) => void;
}

export function renderSettlement(ctx: Ctx) {
  const { available, weeklyCap, pendingHold, lastSettled } =
    settlementSnapshot;
  const { whole, frac } = splitAmount(available);
  const weeklyPct = Math.min(100, (available / weeklyCap) * 100);

  ctx.canvas.innerHTML = `
    <section class="page-head">
      <span class="page-head-watermark"></span>
      <div>
        <span class="page-eyebrow">Customer Funds<span class="page-eyebrow-rule"></span>Settlement</span>
        <h1 class="page-title">Move funds to your business account</h1>
      </div>
      <div class="page-head-meta">
        <span class="page-head-date">Default destination</span>
        <span style="font-size: 0.85rem; font-weight: 600; color: var(--ink-primary); font-variant-numeric: tabular-nums;">${lastSettled?.destination ?? "—"}</span>
      </div>
    </section>

    <section class="grid-2">
      <article class="panel">
        <div class="panel-head">
          <div>
            <div class="panel-eyebrow">Available now</div>
            <h2 class="panel-title">Run a settlement</h2>
            <p class="panel-sub">Move customer-fund balance into your operational account. Cleared funds settle within the day.</p>
          </div>
        </div>

        <div class="panel-body">
          <div style="display: flex; align-items: baseline; gap: 0.4rem; margin-bottom: 0.5rem;">
            <span style="font-size: 0.42em; color: var(--ink-muted); font-weight: 400;">€</span>
            <span style="font-size: clamp(2.6rem, 4.4vw, 3.6rem); font-weight: 200; letter-spacing: -0.04em; line-height: 1; font-variant-numeric: tabular-nums;">${whole}</span>
            <span style="font-size: 0.36em; color: var(--ink-muted); font-weight: 200;">${frac}</span>
          </div>
          <div style="font-size: 0.72rem; color: var(--ink-muted); margin-bottom: 1.5rem; font-variant-numeric: tabular-nums;">Settlement-eligible balance · last reconciled 09:14 CET</div>

          <div style="margin-bottom: 1.75rem;">
            <div style="height: 5px; background: var(--surface-canvas); position: relative;">
              <div style="position: absolute; inset: 0; right: ${100 - weeklyPct}%; background: var(--accent);"></div>
            </div>
            <div style="display: flex; justify-content: space-between; margin-top: 0.45rem; font-size: 0.66rem; color: var(--ink-subtle); font-variant-numeric: tabular-nums; letter-spacing: 0.04em;">
              <span><strong style="color: var(--ink-secondary); font-weight: 700;">${weeklyPct.toFixed(0)}%</strong> of week</span>
              <span>cap ${fmtEur(weeklyCap, { compact: true })} / 7d</span>
            </div>
          </div>

          <div class="form-grid">
            <div class="field form-grid-full">
              <label class="field-label" for="s-amount">Amount to settle</label>
              <input class="field-control is-amount" id="s-amount" value="€ 553,172.40" />
              <span class="field-help">Defaults to full available balance. Pending hold of ${fmtEur(pendingHold, { compact: true })} is excluded.</span>
            </div>
            <div class="field form-grid-full">
              <label class="field-label" for="s-destination">Destination account</label>
              <select class="field-control" id="s-destination">
                <option>Acc. EE38 7700 7710 0000 0417 · Default</option>
                <option>Acc. EE54 8881 1100 4920 0388 · Reserve</option>
                <option>Acc. LT12 7340 0001 8801 7012 · LT-OPS</option>
              </select>
            </div>
            <div class="field form-grid-full">
              <label class="field-label" for="s-window">Clearing window</label>
              <div class="pill-toggle" style="display: grid; grid-template-columns: repeat(3, 1fr);">
                <button class="is-active">Next clearing</button>
                <button>End of day</button>
                <button>Schedule…</button>
              </div>
            </div>
          </div>

          <div style="margin-top: 1.75rem; padding: 1.1rem 1.25rem; background: var(--surface-feature); display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0.85rem; font-variant-numeric: tabular-nums;">
            <div>
              <div style="font-size: 0.58rem; letter-spacing: 0.28em; text-transform: uppercase; font-weight: 700; color: var(--ink-muted);">Fee</div>
              <div style="font-weight: 600; font-size: 0.95rem;">€2.50</div>
            </div>
            <div>
              <div style="font-size: 0.58rem; letter-spacing: 0.28em; text-transform: uppercase; font-weight: 700; color: var(--ink-muted);">Net to operational</div>
              <div style="font-weight: 700; font-size: 1.05rem; color: var(--accent);">€553,169.90</div>
            </div>
            <div>
              <div style="font-size: 0.58rem; letter-spacing: 0.28em; text-transform: uppercase; font-weight: 700; color: var(--ink-muted);">Cleared by</div>
              <div style="font-weight: 600; font-size: 0.95rem;">Today, 17:00 CET</div>
            </div>
          </div>

          <button class="btn btn-primary btn-block" style="margin-top: 1.75rem;">
            Run settlement now ${icon("arrowRight", 11)}
          </button>
        </div>
      </article>

      <article class="panel">
        <div class="panel-head">
          <div>
            <div class="panel-eyebrow">History</div>
            <h2 class="panel-title">Recent settlements</h2>
            <p class="panel-sub">Last 4 weeks of settlement instructions.</p>
          </div>
          <a class="link-arrow" data-route="statements">All statements ${icon("arrowRight", 13)}</a>
        </div>

        <div class="ledger-table">
          <div class="ledger-table-header" style="grid-template-columns: 84px minmax(0, 1fr) 130px 96px;">
            <span>Date</span>
            <span>Settlement</span>
            <span style="text-align: right;">Amount</span>
            <span>Status</span>
          </div>
          ${settlements
            .map(
              (s) => `<div class="ledger-table-row" style="grid-template-columns: 84px minmax(0, 1fr) 130px 96px;">
            <div class="col-date"><strong>${s.date.split(" ").slice(0, 2).join(" ")}</strong>${s.date.split(" ")[2]}</div>
            <div class="col-name" style="min-width: 0;">
              <strong>${s.id}</strong>
              <span>${s.destination}</span>
            </div>
            <div class="col-amount is-out">${fmtEur(s.amount)}</div>
            <div class="col-status"><span class="status-seal is-${s.status}"></span> ${s.statusLabel}</div>
          </div>`,
            )
            .join("")}
        </div>

        <div class="ledger-table-foot">
          <span>Total settled · 30d · <strong style="color: var(--ink-primary); font-weight: 700;">€1,424,860</strong></span>
          <a class="link-arrow" data-route="statements">Download CSV ${icon("download", 13)}</a>
        </div>
      </article>
    </section>
  `;

  ctx.canvas.querySelectorAll<HTMLElement>("[data-route]").forEach((el) => {
    el.addEventListener("click", (e) => {
      e.preventDefault();
      const route = el.getAttribute("data-route") as Route;
      if (route) ctx.navigate(route);
    });
  });

  // Pill toggle
  ctx.canvas
    .querySelectorAll<HTMLButtonElement>(".pill-toggle button")
    .forEach((btn) => {
      btn.addEventListener("click", () => {
        const group = btn.parentElement!;
        group
          .querySelectorAll(".is-active")
          .forEach((b) => b.classList.remove("is-active"));
        btn.classList.add("is-active");
      });
    });
}
